import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Interface for content data (matching Discover.tsx)
interface Content {
  id: string;
  type: 'image' | 'quote';
  content: string;
  mediaPath?: string;
  backgroundColor?: string;
  textColor?: string;
  width?: 1 | 2;
  height?: 1 | 2;
  user?: {
    name: string;
    photoUrl: string;
  };
  hasTextOverlay?: boolean;
}

// Get mock content data (same pattern as in Discover.tsx)
const mockContent: Content[] = Array.from({ length: 100 }, (_, index) => ({
  id: String(index + 1),
  type: 'image',
  content: '',
  mediaPath: `https://picsum.photos/800/1200?random=${index}`,
  width: 1,
  height: 1,
  user: {
    name: `Creator ${index + 1}`,
    photoUrl: `https://picsum.photos/50/50?random=${index + 200}`
  }
}));

// Mock user for the demo
const demoUser = {
  name: 'Adam Sempler',
  username: '@adamIX',
  photoUrl: 'https://picsum.photos/100/100?random=999',
  bio: 'Every day is a new chance to grow, explore, and create. Stay hungry for progress...'
};

const ContentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false);
  
  // Find the content with the matching ID
  const content = mockContent.find(c => c.id === id);
  
  if (!content) {
    return (
      <div className="h-dvh bg-black flex items-center justify-center">
        <div className="bg-gray-900 p-8 rounded-lg shadow-md text-center text-white">
          <h2 className="text-2xl font-bold mb-4">Content Not Found</h2>
          <p className="mb-4">Sorry, we couldn't find the content you're looking for.</p>
          <button 
            onClick={() => navigate('/discover')}
            className="text-blue-400 hover:text-blue-300"
          >
            Back to Discover
          </button>
        </div>
      </div>
    );
  }
  
  const handleLike = () => {
    setLiked(!liked);
  };
  
  return (
    <div className="relative h-dvh bg-black">
      {/* Status bar */}
      <div className="h-10 flex items-center justify-between px-4 text-white">
        <div className="text-base font-semibold">8:24</div>
        <div className="flex items-center space-x-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
          </svg>
          <div className="ml-1 bg-white w-6 h-3 rounded-sm"></div>
        </div>
      </div>
      
      {/* Main content */}
      <div className="w-full h-[calc(100vh-10rem)] relative">
        {/* Full-screen image */}
        <img 
          src={content.mediaPath} 
          alt="Content" 
          className="w-full h-full object-cover"
        />
        
        {/* Navigation controls overlay */}
        <div className="absolute top-0 left-0 right-0 p-4 flex justify-between">
          <button 
            onClick={() => navigate('/discover')}
            className="w-10 h-10 flex items-center justify-center text-white"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <button className="w-10 h-10 flex items-center justify-center text-white">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          </button>
        </div>
        
        {/* User info overlay at bottom */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent pt-20 px-4 pb-4">
          <div className="flex items-center">
            <div className="relative">
              <img 
                src={demoUser.photoUrl} 
                alt={demoUser.name}
                className="w-12 h-12 rounded-full border-2 border-white object-cover"
              />
              <div className="absolute -right-1 -bottom-1 bg-gradient-to-r from-orange-500 to-pink-500 rounded-full p-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-white text-lg font-bold">{demoUser.name} <span className="text-gray-300 font-normal">{demoUser.username}</span></h3>
              <p className="text-white text-sm">
                {demoUser.bio}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom action bar */}
      <div className="h-20 px-4 py-2 flex justify-between items-center bg-black">
        <button className="text-white/60 px-4 py-2 rounded-full bg-white/20 flex items-center">
          <span className="text-sm">Quick com</span>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
        </button>
        
        <div className="flex items-center">
          <button 
            onClick={handleLike} 
            className="flex flex-col items-center justify-center mx-4"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className={`h-8 w-8 ${liked ? 'text-red-500' : 'text-white'}`} viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
            </svg>
            <span className="text-white text-sm">429K</span>
          </button>
          
          <button className="flex flex-col items-center justify-center mx-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span className="text-white text-sm">17K</span>
          </button>
          
          <button className="flex flex-col items-center justify-center mx-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="text-white text-sm">4</span>
          </button>
        </div>
      </div>
      
      {/* Bottom progress bar */}
      <div className="h-1 bg-gray-800">
        <div className="h-full bg-white w-1/3"></div>
      </div>
    </div>
  );
};

export default ContentDetail; 